import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import {
  deviceProfilesApi,
  devicePropertiesApi,
  type DeviceProfileCreate,
  type DeviceProfileUpdate,
} from '../api/deviceProfiles'

export function useDeviceProfiles() {
  return useQuery({
    queryKey: ['device-profiles'],
    queryFn: () => deviceProfilesApi.list(),
  })
}

export function useDeviceProfile(id: string | undefined) {
  return useQuery({
    queryKey: ['device-profile', id],
    queryFn: () => deviceProfilesApi.get(id!),
    enabled: !!id,
  })
}

export function useDeviceSchema(deviceId: string | undefined) {
  return useQuery({
    queryKey: ['device-schema', deviceId],
    queryFn: () => devicePropertiesApi.schema(deviceId!),
    enabled: !!deviceId,
  })
}

export function useDeviceProperties(deviceId: string | undefined) {
  return useQuery({
    queryKey: ['device-properties', deviceId],
    queryFn: () => devicePropertiesApi.get(deviceId!),
    enabled: !!deviceId,
    refetchInterval: 15000,
  })
}

export function useUpdateDeviceProperties(deviceId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      properties,
      apiKey,
    }: {
      properties: Record<string, unknown>
      apiKey?: string
    }) => devicePropertiesApi.update(deviceId!, properties, apiKey),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['device-properties', deviceId] })
      qc.invalidateQueries({ queryKey: ['device-properties-history', deviceId] })
      message.success('属性已更新')
    },
    onError: (err: Error) => message.error(err.message),
  })
}

export function useDevicePropertiesHistory(deviceId: string | undefined) {
  return useQuery({
    queryKey: ['device-properties-history', deviceId],
    queryFn: () => devicePropertiesApi.history(deviceId!),
    enabled: !!deviceId,
  })
}

export function useProfileMap() {
  return useQuery({
    queryKey: ['device-profiles'],
    queryFn: () => deviceProfilesApi.list(),
    select: (profiles) => {
      const map: Record<string, string> = {}
      for (const p of profiles) {
        map[p.id] = p.display_name
      }
      return map
    },
  })
}

// ── Profile CRUD mutations ──────────────────────────────────────────

export function useCreateDeviceProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: DeviceProfileCreate) => deviceProfilesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['device-profiles'] })
      message.success('型号已创建')
    },
    onError: (err: Error) => message.error(err.message),
  })
}

export function useUpdateDeviceProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: DeviceProfileUpdate }) =>
      deviceProfilesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['device-profiles'] })
      message.success('型号已更新')
    },
    onError: (err: Error) => message.error(err.message),
  })
}

export function useDeleteDeviceProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deviceProfilesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['device-profiles'] })
      message.success('型号已删除')
    },
    onError: (err: Error) => message.error(err.message),
  })
}
